/**
 * TemplateLibrary — the campaign's template picker: two sections (Brand,
 * This campaign) of cards, plus `New template ▾` (blank, or forked from the
 * library). Campaign and brand rows need outreach:write to edit or archive.
 * The global templates are not listed (owner, 2026-10-21): the Discovr default
 * runs in the background and is offered only inside the "From library" picker.
 *
 * Search/Sort/Filters (Figma header bar) are all client-side — the groups
 * are already fully loaded, so there's no BE param to wire, unlike the
 * Inbox's equivalent row.
 */
import { useMemo, useState } from 'react';
import type { OutreachTemplate, OutreachTemplateGroups, OutreachTemplateKind } from '@discovr/types';
import { CaretDown, InstagramLogo, MagnifyingGlass, Plus, SlidersHorizontal } from '../../../design-system/icons';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import gmailLogo from '../../../assets/connectors/gmail.png';
import { KIND_LABELS, TemplateCard } from './TemplateCard';
import { TemplatePicker } from './TemplatePicker';

const SECTIONS: { key: 'brand' | 'campaign'; title: string }[] = [
  { key: 'brand', title: 'Brand' },
  { key: 'campaign', title: 'This campaign' },
];

type SortKey = 'updated' | 'name';
const SORTS: { value: SortKey; label: string }[] = [
  { value: 'updated', label: 'Recently updated' },
  { value: 'name', label: 'Name (A–Z)' },
];

type KindFilter = 'all' | OutreachTemplateKind;
const KIND_FILTERS: { value: KindFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'sequence', label: KIND_LABELS.sequence },
  { value: 'after_reply', label: KIND_LABELS.after_reply },
  { value: 'other', label: KIND_LABELS.other },
];

export interface TemplateLibraryProps {
  groups: OutreachTemplateGroups;
  canWrite: boolean;
  onNew: () => void;
  onOpen: (t: OutreachTemplate) => void;
  onEdit: (t: OutreachTemplate) => void;
  onFork: (t: OutreachTemplate) => void;
  onArchive: (t: OutreachTemplate) => void;
  onRestore: (t: OutreachTemplate) => void;
}

export function TemplateLibrary({ groups, canWrite, onNew, onOpen, onEdit, onFork, onArchive, onRestore }: TemplateLibraryProps) {
  const [picking, setPicking] = useState(false);
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState<SortKey>('updated');
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');

  // An archived row can't be changed or picked (BE), so it only opens read-only.
  const canEdit = (t: OutreachTemplate) => canWrite && t.scope !== 'global' && !t.archivedAt;

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    const bySection = (list: OutreachTemplate[]) =>
      list
        .filter((t) => (!q || t.name.toLowerCase().includes(q)) && (kindFilter === 'all' || t.kind === kindFilter))
        .sort((a, b) =>
          sort === 'name' ? a.name.localeCompare(b.name) : new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
        );
    return { brand: bySection(groups.brand), campaign: bySection(groups.campaign) };
  }, [groups, query, sort, kindFilter]);

  const sortLabel = SORTS.find((option) => option.value === sort)?.label ?? sort;
  const filtered = kindFilter !== 'all';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="m-0 text-[22px]/[28px] font-medium text-foreground">Templates</h1>
          <p className="mt-1 text-body-sm-regular text-muted-foreground">
            Reusable copy for every send.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-64">
            <MagnifyingGlass size={16} className="absolute top-1/2 left-3 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search templates"
              className="h-10 rounded-lg border-border bg-card pl-9"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={`Sort by: ${sortLabel}`}
                className="flex h-10 shrink-0 items-center gap-2 rounded-lg border border-border bg-card px-3.5 text-body-sm-medium text-foreground transition-colors hover:bg-muted"
              >
                Sort by <CaretDown size={14} />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuRadioGroup value={sort} onValueChange={(value) => setSort(value as SortKey)}>
                {SORTS.map((option) => (
                  <DropdownMenuRadioItem key={option.value} value={option.value}>
                    {option.label}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={filtered ? 'Filters: 1 active' : 'Filters'}
                className="flex h-10 shrink-0 items-center gap-2 rounded-lg border border-border bg-card px-3.5 text-body-sm-medium text-foreground transition-colors hover:bg-muted"
              >
                <SlidersHorizontal size={16} /> Filters{filtered ? ' · 1' : ''}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuRadioGroup value={kindFilter} onValueChange={(value) => setKindFilter(value as KindFilter)}>
                {KIND_FILTERS.map((option) => (
                  <DropdownMenuRadioItem key={option.value} value={option.value}>
                    {option.label}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuContent>
          </DropdownMenu>
          {canWrite ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="rounded-full">
                  <Plus size={14} /> New template <CaretDown size={14} className="ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onSelect={onNew}>
                  <img src={gmailLogo} alt="" className="h-4 w-auto" /> Email template
                </DropdownMenuItem>
                <DropdownMenuItem disabled title="Instagram DM — later phase">
                  <InstagramLogo /> Instagram DM template
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => setPicking(true)}>From library</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </div>
      </div>

      {SECTIONS.map((s) => {
        const list = visible[s.key];
        const isEmptyOutright = groups[s.key].length === 0;
        return (
          <section key={s.key} className="space-y-3">
            <h2 className="text-body-md-medium text-foreground">{s.title}</h2>
            {list.length === 0 ? (
              <p className="text-body-sm-regular text-muted-foreground">
                {isEmptyOutright ? 'Nothing here yet.' : 'No templates match your search or filters.'}
              </p>
            ) : (
              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
                {list.map((t) => (
                  <TemplateCard
                    key={t.id}
                    template={t}
                    showMenu={canWrite}
                    canEdit={canEdit(t)}
                    canFork={canWrite && t.scope !== 'campaign'}
                    onOpen={() => onOpen(t)}
                    onEdit={() => onEdit(t)}
                    onFork={() => onFork(t)}
                    onArchive={() => onArchive(t)}
                    onRestore={t.archivedAt && t.scope !== 'global' ? () => onRestore(t) : undefined}
                  />
                ))}
              </div>
            )}
          </section>
        );
      })}

      <TemplatePicker
        open={picking}
        groups={groups}
        onClose={() => setPicking(false)}
        onPick={(t) => {
          setPicking(false);
          onFork(t);
        }}
      />
    </div>
  );
}
